import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import type { Filters, LostEvent, Reason } from './types';

/**
 * Lista de eventos com paginação por CURSOR (não offset — PRD §9-A: a tabela só
 * cresce), infinite scroll e atualização OTIMISTA da auditoria: o card sai da
 * fila na hora e só volta ao estado anterior se a escrita falhar (PRD §7.1).
 */
export function useEventList(
  filters: Filters,
  tab: 'fila' | 'confirmados',
  reasons: Reason[],
  /** `false` segura a busca — usado por painel fechado, que não deve pagar request. */
  enabled = true,
) {
  const [items, setItems] = useState<LostEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const seq = useRef(0);

  const key = JSON.stringify({ filters, tab, enabled });

  const loadFirst = useCallback(async () => {
    const mine = ++seq.current;
    if (!enabled) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const res = await api.listEvents(filters, tab);
      if (mine !== seq.current) return;
      setItems(res.items); setCursor(res.next_cursor);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(err instanceof ApiError ? err.message : 'Falha ao ler revops_lost_events.');
      setItems([]); setCursor(null);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { void loadFirst(); }, [loadFirst]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const res = await api.listEvents(filters, tab, cursor);
      if (mine !== seq.current) return;
      // Dedup por id: um evento auditado entre duas páginas pode reaparecer,
      // e chave duplicada no React quebraria a lista silenciosamente.
      setItems((prev) => {
        const seen = new Set(prev.map((e) => e.id));
        return [...prev, ...res.items.filter((e) => !seen.has(e.id))];
      });
      setCursor(res.next_cursor);
    } catch {
      /* falha ao paginar não derruba o que já está na tela */
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, loadingMore, key]);

  const setBusy = (id: number, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  /** Alterna Em Análise ⇄ Real Lost. Otimista: some da aba na hora; reverte com erro. */
  const toggleAudit = useCallback(async (ev: LostEvent, onError: (msg: string) => void) => {
    if (busyIds.has(ev.id)) return;
    const target = ev.audit_status === 'real_lost' ? 'em_analise' : 'real_lost';
    const snapshot = items;

    setBusy(ev.id, true);
    setItems((prev) => prev.filter((e) => e.id !== ev.id));

    try {
      await api.auditEvent(ev.id, {
        audit_status: target, reason_ids: ev.reason_ids?.length ? ev.reason_ids : (ev.reason_id != null ? [ev.reason_id] : []), audit_note: ev.audit_note,
      });
    } catch (err) {
      setItems(snapshot);
      onError(err instanceof ApiError ? err.message : 'Falha ao gravar a auditoria.');
    } finally {
      setBusy(ev.id, false);
    }
  }, [items, busyIds]);

  /** Classifica sem mudar o status. Lista ordenada de tags: a primeira é o motivo principal. */
  const setReason = useCallback(async (ev: LostEvent, picked: Reason[], onError: (msg: string) => void) => {
    const snapshot = items;
    const primary = picked[0] ?? null;
    setItems((prev) => prev.map((e) => e.id === ev.id
      ? { ...e, reason_id: primary?.id ?? null, reason_label: primary?.label ?? null, reason_is_exception: primary?.is_exception ?? null, reason_ids: picked.map((r) => r.id) }
      : e));
    try {
      await api.auditEvent(ev.id, {
        audit_status: ev.audit_status, reason_ids: picked.map((r) => r.id), audit_note: ev.audit_note,
      });
    } catch (err) {
      setItems(snapshot);
      onError(err instanceof ApiError ? err.message : 'Falha ao classificar.');
    }
  }, [items]);

  /** Reclassificação manual churn ⇄ contraction — otimista, mesmo padrão de `setReason`. */
  const setCorrection = useCallback(async (
    ev: LostEvent,
    body: { corrected_event_type: 'churn' | 'contraction'; corrected_mrr_lost: number; note?: string | null },
    onError: (msg: string) => void,
  ) => {
    const snapshot = items;
    setItems((prev) => prev.map((e) => e.id === ev.id
      ? { ...e, corrected_event_type: body.corrected_event_type, corrected_mrr_lost: body.corrected_mrr_lost, correction_note: body.note ?? null }
      : e));
    try {
      await api.setEventCorrection(ev.id, body);
    } catch (err) {
      setItems(snapshot);
      onError(err instanceof ApiError ? err.message : 'Falha ao salvar a reclassificação.');
    }
  }, [items]);

  const clearCorrection = useCallback(async (ev: LostEvent, onError: (msg: string) => void) => {
    const snapshot = items;
    setItems((prev) => prev.map((e) => e.id === ev.id
      ? { ...e, corrected_event_type: null, corrected_mrr_lost: null, correction_note: null }
      : e));
    try {
      await api.clearEventCorrection(ev.id);
    } catch (err) {
      setItems(snapshot);
      onError(err instanceof ApiError ? err.message : 'Falha ao remover a reclassificação.');
    }
  }, [items]);

  return {
    items, loading, loadingMore, error, hasMore: cursor !== null, busyIds,
    reload: loadFirst, loadMore, toggleAudit, setReason, setCorrection, clearCorrection, reasons,
  };
}
